import { useState } from 'react';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { Tooltip } from '@/shared/components/Tooltip';
import { RAISE_SERVICE_REQUEST_LABELS as L } from '../../constants/labels';
import { SHARED_MESSAGES } from '@shared/constants/messages';
import type { ServiceType } from '../../types/serviceTypeTypes';
import type { FacilityListItem } from '@/epics/facilityManagement/types/facilityListType';
import type { EquipmentListItem } from '@/epics/facilityManagement/api/listEquipmentApiService';

interface Step1Props {
  serviceType: ServiceType | null;
  serviceTypeLoading: boolean;

  facilities: FacilityListItem[];
  facilitiesLoading: boolean;
  facilitiesHasNextPage: boolean;
  selectedFacilityId: string | null;
  selectedFacilityName: string;
  facilityError: string;
  canListFacilities: boolean;
  privilegesLoading: boolean;
  onFacilitySearchChange: (query: string) => void;

  equipment: EquipmentListItem[];
  equipmentLoading: boolean;
  equipmentHasNextPage: boolean;
  selectedEquipmentId: string | null;
  selectedEquipmentName: string;
  canListEquipment: boolean;
  isEquipmentLoadingForFacility: boolean;
  onEquipmentSearchChange: (query: string) => void;

  onFacilitySelect: (id: string) => void;
  onEquipmentSelect: (id: string) => void;
  onFacilitiesScrollEnd: () => void;
  onEquipmentScrollEnd: () => void;
}

const Step1FacilityEquipment: React.FC<Step1Props> = ({
  serviceType,
  serviceTypeLoading,
  facilities,
  facilitiesLoading,
  facilitiesHasNextPage,
  selectedFacilityId,
  selectedFacilityName,
  facilityError,
  canListFacilities,
  privilegesLoading,
  onFacilitySearchChange,
  equipment,
  equipmentLoading,
  equipmentHasNextPage,
  selectedEquipmentId,
  selectedEquipmentName,
  canListEquipment,
  isEquipmentLoadingForFacility,
  onEquipmentSearchChange,
  onFacilitySelect,
  onEquipmentSelect,
  onFacilitiesScrollEnd,
  onEquipmentScrollEnd,
}) => {
  const [facilityDropdownOpen, setFacilityDropdownOpen] = useState(false);
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);

  const facilityItems = facilities.map((f) => ({
    id: f.id,
    label: `${f.name} [${f.facilityId}]`,
  }));
  const equipmentItems = equipment.map((e) => ({
    id: e.id,
    label: `${e.name} [${e.equipmentId}]`,
  }));

  const isInitialFacilitiesLoading =
    facilitiesLoading && facilities.length === 0;
  const isLoadingMoreFacilities = facilitiesLoading && facilities.length > 0;

  const isInitialEquipmentLoading = equipmentLoading && equipment.length === 0;
  const isLoadingMoreEquipment = equipmentLoading && equipment.length > 0;

  // Don't disable while the dropdown is open (user is searching) or when a value is already
  // selected (post-selection re-fetches should not mute the trigger).
  const facilityDisabled =
    !canListFacilities ||
    (isInitialFacilitiesLoading &&
      !facilityDropdownOpen &&
      !selectedFacilityId);
  const equipmentDisabled =
    !selectedFacilityId ||
    !canListEquipment ||
    (isInitialEquipmentLoading &&
      !equipmentDropdownOpen &&
      !selectedEquipmentId);

  const facilityMenuItems = (() => {
    if (isInitialFacilitiesLoading) {
      return [{ id: '__loading__', label: L.FACILITY_LOADING, disabled: true }];
    }
    if (facilityItems.length === 0) {
      return [
        {
          id: '__no_results__',
          label: SHARED_MESSAGES.DROPDOWN_NO_RESULTS,
          disabled: true,
        },
      ];
    }
    return [
      ...facilityItems,
      ...(isLoadingMoreFacilities
        ? [
            {
              id: '__loading_more__',
              label: L.FACILITY_LOADING,
              disabled: true,
            },
          ]
        : []),
    ];
  })();

  const equipmentMenuItems = (() => {
    if (isInitialEquipmentLoading) {
      return [
        { id: '__loading__', label: L.EQUIPMENT_LOADING, disabled: true },
      ];
    }
    if (equipmentItems.length === 0 && selectedFacilityId) {
      return [
        {
          id: '__no_results__',
          label: SHARED_MESSAGES.DROPDOWN_NO_RESULTS,
          disabled: true,
        },
      ];
    }
    return [
      ...equipmentItems,
      ...(isLoadingMoreEquipment
        ? [
            {
              id: '__loading_more__',
              label: L.EQUIPMENT_LOADING,
              disabled: true,
            },
          ]
        : []),
    ];
  })();

  const facilityDropdown = (
    <Dropdown
      menuFullWidth
      toolTip={false}
      showSearch
      searchPlaceholder="Szukaj placowki"
      menuMaxVisibleItems={6}
      items={facilityMenuItems}
      {...(selectedFacilityId != null
        ? { selectedId: selectedFacilityId }
        : {})}
      placement="bottom-start"
      onOpenChange={(isOpen) => {
        setFacilityDropdownOpen(isOpen);
        if (!isOpen) {
          onFacilitySearchChange('');
        }
      }}
      onItemClick={(id) => {
        if (
          id !== '__loading__' &&
          id !== '__no_results__' &&
          id !== '__loading_more__'
        ) {
          onFacilitySelect(id);
        }
      }}
      {...(facilitiesHasNextPage || isLoadingMoreFacilities
        ? { onScrollEnd: onFacilitiesScrollEnd }
        : {})}
      onSearchChange={onFacilitySearchChange}
      disabled={facilityDisabled}
      error={Boolean(facilityError)}
      errorMessage={facilityError}
      trigger={({ open, toggle, disabled: d, readOnly, error: e }) => (
        <DropDownButton
          label={L.FIELD_FACILITY}
          data-testid="facility-dropdown"
          value={selectedFacilityName}
          open={open}
          toggle={toggle}
          disabled={d}
          readOnly={readOnly ?? false}
          error={e}
        />
      )}
    />
  );

  const equipmentDropdown = (
    <Dropdown
      menuFullWidth
      toolTip={false}
      showSearch
      searchPlaceholder="Szukaj aparatury"
      menuMaxVisibleItems={6}
      items={equipmentMenuItems}
      {...(selectedEquipmentId != null
        ? { selectedId: selectedEquipmentId }
        : {})}
      placement="top-start"
      onOpenChange={(isOpen) => {
        setEquipmentDropdownOpen(isOpen);
        if (!isOpen) {
          onEquipmentSearchChange('');
        }
      }}
      onItemClick={(id) => {
        if (
          id !== '__loading__' &&
          id !== '__no_results__' &&
          id !== '__loading_more__'
        ) {
          onEquipmentSelect(id);
        }
      }}
      {...(equipmentHasNextPage || isLoadingMoreEquipment
        ? { onScrollEnd: onEquipmentScrollEnd }
        : {})}
      onSearchChange={onEquipmentSearchChange}
      disabled={equipmentDisabled}
      trigger={({ open, toggle, disabled: d, readOnly, error: e }) => (
        <DropDownButton
          data-testid="equipment-dropdown"
          label={
            <>
              <span
                style={{
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                {L.FIELD_EQUIPMENT}{' '}
              </span>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                {L.FIELD_EQUIPMENT_OPTIONAL}
              </span>
            </>
          }
          value={selectedEquipmentName}
          open={open}
          toggle={toggle}
          disabled={d}
          readOnly={readOnly ?? false}
          error={e}
        />
      )}
    />
  );

  return (
    <div className="flex flex-col gap-8 mt-3">
      {/* Selected service card */}
      <div className="flex flex-col gap-3">
        <p className="text-[18px] text-on-surface-variant">
          {L.SECTION_SERVICE}
        </p>
        <div className="border border-[#DCDDE0] rounded-2xl p-4 flex flex-col gap-3">
          {serviceTypeLoading ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F3F4F6] animate-pulse shrink-0" />
              <div className="flex flex-col gap-1 flex-1">
                <div className="h-4 w-40 bg-[#F3F4F6] rounded animate-pulse" />
                <div className="h-3 w-16 bg-[#F3F4F6] rounded animate-pulse" />
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F3F4F6] shrink-0">
                  <WrenchScrewdriverIcon className="w-5 h-5 text-[#474A4C]" />
                </span>
                <div className="flex-1">
                  <p className="font-medium text-[#181A1B] text-sm">
                    {serviceType?.service_name ?? '-'}
                  </p>
                  <p className="text-sm text-[#474A4C]">
                    {L.SERVICE_PRICE_PLACEHOLDER}
                  </p>
                </div>
              </div>
              {serviceType?.description && (
                <p className="text-sm text-[#474A4C]">
                  {serviceType.description}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="border-b-2 border-[#DCDDE0]" />

      {/* Facility section */}
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-[18px] text-on-surface-variant">
            {L.SECTION_FACILITY}
          </p>
          <p className="text-sm text-[#474A4C]">{L.FACILITY_DESCRIPTION}</p>
        </div>
        <div className="max-w-[600px]">
          {privilegesLoading ? (
            <div className="h-14 rounded-lg bg-[#E5E7E9] animate-pulse" />
          ) : !canListFacilities ? (
            <Tooltip
              content={L.FACILITY_DISABLED_TOOLTIP}
              forceShow
              side="top"
              align="center"
              contentMaxWidth={280}
              triggerWrapContent
            >
              {facilityDropdown}
            </Tooltip>
          ) : (
            facilityDropdown
          )}
        </div>
      </div>

      <div className="border-b-2 border-[#DCDDE0]" />

      {/* Equipment section */}
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-[18px] text-on-surface-variant">
            {L.SECTION_EQUIPMENT}
          </p>
          <p className="text-sm text-[#474A4C]">{L.EQUIPMENT_DESCRIPTION}</p>
        </div>
        <div className="max-w-[600px]">
          {privilegesLoading ? (
            <div className="h-14 rounded-lg bg-[#E5E7E9] animate-pulse" />
          ) : !canListEquipment ? (
            <Tooltip
              content={L.EQUIPMENT_DISABLED_TOOLTIP}
              forceShow
              side="top"
              align="center"
              contentMaxWidth={280}
              triggerWrapContent
            >
              {equipmentDropdown}
            </Tooltip>
          ) : !selectedFacilityId ? (
            <Tooltip
              content={L.EQUIPMENT_SELECT_FACILITY_FIRST}
              forceShow
              side="top"
              align="center"
              contentMaxWidth={280}
            >
              {equipmentDropdown}
            </Tooltip>
          ) : isEquipmentLoadingForFacility ? (
            <div className="h-14 rounded-lg bg-[#E5E7E9] animate-pulse cursor-not-allowed" />
          ) : (
            equipmentDropdown
          )}
        </div>
      </div>
    </div>
  );
};

export default Step1FacilityEquipment;
